import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Badge, Button, Card, Dot, EmptyState, IconButton, PageHeader, Row, Segmented, categoryColor, useToast } from "@plataforma/design-system";
import type { Appointment, AppointmentStatus } from "@plataforma/shared";
import { useAppConfig } from "../../app/config";
import { errorMessage } from "../../lib/api";
import { addDays, todayIso, weekDates } from "../../lib/format";
import { appointmentStatusLabel, appointmentStatusTone } from "../../lib/labels";
import { useClient } from "../clients/api";
import { useInvoices } from "../invoices/api";
import { useInvoiceFlow } from "../invoices/useInvoiceFlow";
import { useAppointments, useChangeStatus, useLocations } from "./api";
import { AppointmentDetail } from "./AppointmentDetail";
import { AppointmentForm } from "./AppointmentForm";
import "./agenda.css";

type Mode = "day" | "week";

export function AgendaPage() {
  const { format, has } = useAppConfig();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [date, setDate] = useState(todayIso());
  const [mode, setMode] = useState<Mode>("day");
  const [form, setForm] = useState<{ appointment?: Appointment; date?: string } | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  const dates = mode === "day" ? [date] : weekDates(date);
  const from = dates[0] as string;
  const to = dates.at(-1) as string;
  const appointments = useAppointments(from, to);
  const locations = useLocations();
  // As notas são outra funcionalidade: só entram na agenda quando existem nesta aplicação.
  const invoices = useInvoices({}, has("invoices"));
  const changeStatus = useChangeStatus();
  const pendingInvoices = (invoices.data ?? []).filter((invoice) => invoice.status === "pending");
  const flow = useInvoiceFlow(pendingInvoices);

  // Atalho vindo do perfil do cliente: /agenda?novo=<id> abre o formulário já com o cliente.
  const presetClientId = searchParams.get("novo") ?? undefined;
  const presetClient = useClient(presetClientId);
  const openForm = form ?? (presetClientId && presetClient.data ? { date } : null);
  const closeForm = () => {
    setForm(null);
    if (presetClientId) setSearchParams({}, { replace: true });
  };

  const items = appointments.data ?? [];
  const today = todayIso();
  const invoiceOf = (appointmentId: string) => (invoices.data ?? []).filter((invoice) => invoice.appointmentId === appointmentId).find((invoice) => invoice.isCurrent);
  const colorOf = (locationId: string) => categoryColor((locations.data ?? []).findIndex((location) => location.id === locationId));
  const count = (status: AppointmentStatus) => items.filter((item) => item.status === status).length;
  const places = [...new Map(items.filter((item) => item.status !== "cancelled" && item.status !== "no_show").map((item) => [item.locationId, item.locationName])).entries()];
  const scope = mode === "week" ? "nesta semana" : date === today ? "hoje" : "nesta data";
  const detail = items.find((item) => item.id === detailId);
  const move = (direction: number) => setDate(addDays(date, direction * (mode === "day" ? 1 : 7)));

  async function setStatus(appointment: Appointment, status: AppointmentStatus) {
    try {
      await changeStatus.mutateAsync({ id: appointment.id, status });
      setDetailId(null);
      toast(status === "completed" && has("invoices") ? "Consulta realizada. A nota fiscal está pronta para emitir." : "Situação da consulta atualizada.", status === "completed" && has("invoices") ? { label: "Ver notas", run: () => navigate("/notas-fiscais") } : undefined);
    } catch (failure) {
      toast(errorMessage(failure));
    }
  }

  const quickAction = (item: Appointment) => {
    const invoice = invoiceOf(item.id);
    if (item.status === "scheduled") return <Button size="small" onClick={() => setStatus(item, "confirmed")}>Confirmar</Button>;
    if (item.status === "confirmed" && item.date <= today) return <Button size="small" onClick={() => setStatus(item, "completed")}>Realizada</Button>;
    if (item.status === "completed" && invoice?.status === "pending") return <Button size="small" onClick={() => flow.open(invoice, "emit")}>Emitir nota</Button>;
    return null;
  };

  return (
    <>
      <PageHeader title="Agenda" actions={<Button variant="primary" onClick={() => setForm({ date })}>＋ Nova consulta</Button>} />
      <div className="agenda-summary">
        <span><strong>{items.length}</strong> {items.length === 1 ? "consulta" : "consultas"} {scope}</span>
        {items.length ? <><span>Confirmadas <strong>{count("confirmed")}</strong></span><span>Realizadas <strong>{count("completed")}</strong></span></> : null}
        {places.length ? <span>{places.map(([id, name]) => <span key={id} style={{ marginRight: 10, whiteSpace: "nowrap" }}><Dot color={colorOf(id)} />{name}</span>)}</span> : null}
        {pendingInvoices.length ? <button type="button" className="agenda-summary__link" onClick={() => navigate("/notas-fiscais")}>{pendingInvoices.length} {pendingInvoices.length === 1 ? "nota fiscal pendente" : "notas fiscais pendentes"} →</button> : null}
      </div>
      <Card>
        <div className="agenda-toolbar">
          <Row>
            <IconButton label="Período anterior" onClick={() => move(-1)}>‹</IconButton>
            <div className="agenda-toolbar__title">{mode === "day" ? format.longDate(date) : `${format.dayMonth(from)} – ${format.dayMonth(to)}`}</div>
            <IconButton label="Próximo período" onClick={() => move(1)}>›</IconButton>
            <Button size="small" onClick={() => setDate(today)}>Hoje</Button>
          </Row>
          <Segmented label="Visualização da agenda" value={mode} onChange={setMode} options={[{ value: "day", label: "Dia" }, { value: "week", label: "Semana" }]} />
        </div>
        {mode === "day" ? (
          items.length === 0 ? (
            <EmptyState title={appointments.isPending ? undefined : "Nenhuma consulta nesta data"}>
              {appointments.isPending ? "Carregando…" : <Button size="small" variant="primary" onClick={() => setForm({ date })}>＋ Nova consulta</Button>}
            </EmptyState>
          ) : (
            items.map((item) => (
              <div key={item.id} className="agenda-row" role="button" tabIndex={0} data-cancelled={item.status === "cancelled"} aria-label={`Abrir consulta de ${item.clientName}`}
                onClick={() => setDetailId(item.id)} onKeyDown={(event) => { if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); setDetailId(item.id); } }}>
                <div><div className="agenda-row__time">{item.time}</div><div className="ds-muted ds-small">{item.durationMinutes} min</div></div>
                <div><div className="agenda-row__name">{item.clientName}</div><div className="ds-muted ds-small">{item.type} · {format.money(item.value)}</div></div>
                <div className="ds-small"><Dot color={colorOf(item.locationId)} />{item.locationName}</div>
                <div><Badge tone={appointmentStatusTone[item.status]}>{appointmentStatusLabel[item.status]}</Badge></div>
                <div className="agenda-row__action" onClick={(event) => event.stopPropagation()}>{quickAction(item)}</div>
              </div>
            ))
          )
        ) : (
          <div className="agenda-week">
            {dates.map((day) => (
              <div key={day} className="agenda-week__day">
                <div className="agenda-week__head" data-today={day === today}>
                  <span>{format.weekday(day)} {format.dayMonth(day)}</span>
                  <button type="button" className="agenda-week__add" aria-label={`Marcar consulta em ${format.longDate(day)}`} onClick={() => setForm({ date: day })}>＋</button>
                </div>
                {items.filter((item) => item.date === day).map((item) => (
                  <button key={item.id} type="button" className="agenda-week__event" data-status={item.status} style={{ ["--dot-color" as string]: colorOf(item.locationId) }} onClick={() => setDetailId(item.id)}>
                    <strong>{item.time} {item.clientName}</strong>
                    <span className="ds-muted">{item.locationName} · {appointmentStatusLabel[item.status]}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
        {(locations.data ?? []).length ? <div className="agenda-legend">{(locations.data ?? []).map((location, index) => <span key={location.id}><Dot color={categoryColor(index)} />{location.name}</span>)}</div> : null}
      </Card>

      {openForm ? <AppointmentForm appointment={openForm.appointment} defaults={{ date: openForm.date, client: presetClient.data?.client }} onClose={closeForm} onSaved={(savedDate) => setDate(savedDate)} /> : null}
      {detail && !openForm ? (
        <AppointmentDetail
          appointment={detail}
          invoice={invoiceOf(detail.id)}
          locationColor={colorOf(detail.locationId)}
          onStatus={(status) => setStatus(detail, status)}
          onEdit={() => { setForm({ appointment: detail }); setDetailId(null); }}
          onInvoice={(invoice, step) => { setDetailId(null); flow.open(invoice, step); }}
          onClose={() => setDetailId(null)}
        />
      ) : null}
      {flow.dialog}
    </>
  );
}
